import { PlusRegular } from "@icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import FilterMenuContent from "../menu";
import {
  ComparisonFilterValue,
  CompoundCondition,
  CompoundFilterGroup,
  FilterMenuItem,
  LogicalOperator,
  TextOperator,
} from "../menu/types";
import { CompoundFilterEntry, FilterEntry } from "../use-filter-state";
import CompoundFilterChip from "./compound-filter-chip";
import FilterChip from "./filter-chip";

interface FilterBarProps {
  filterMenuItemGroups: FilterMenuItem[][];
  activeFilterEntries: FilterEntry[];
  selectedValues: Record<string, string[]>;
  comparisonValues: Record<string, ComparisonFilterValue>;
  booleanValues: Record<string, string>;
  onSelectionChange: (name: string, values: string[]) => void;
  onComparisonChange: (name: string, value: ComparisonFilterValue) => void;
  onBooleanChange: (name: string, value: string) => void;
  onTextOperatorChange: (name: string, operator: TextOperator) => void;
  onRemoveFilter: (fieldName: string) => void;
  onRemoveCompoundGroup: (groupId: string) => void;
  onRemoveCompoundCondition: (groupId: string, conditionIndex: number) => void;
  onToggleCompoundLogic: (groupId: string) => void;
  onSetCompoundCondition: (
    groupId: string,
    index: number,
    condition: CompoundCondition,
  ) => void;
  onAddConditionToGroup: (
    groupId: string,
    condition: CompoundCondition,
  ) => void;
  onCreateCompoundGroup: (group: CompoundFilterGroup) => void;
  onClearAll: () => void;
}

export default function FilterBar({
  filterMenuItemGroups,
  activeFilterEntries,
  selectedValues,
  comparisonValues,
  booleanValues,
  onSelectionChange,
  onComparisonChange,
  onBooleanChange,
  onTextOperatorChange,
  onRemoveFilter,
  onRemoveCompoundGroup,
  onRemoveCompoundCondition,
  onToggleCompoundLogic,
  onSetCompoundCondition,
  onAddConditionToGroup,
  onCreateCompoundGroup,
  onClearAll,
}: FilterBarProps) {
  const { t } = useTranslation();
  const [addFilterOpen, setAddFilterOpen] = useState(false);

  if (activeFilterEntries.length === 0) return null;

  const existingCompoundLogics: LogicalOperator[] = activeFilterEntries
    .filter((e): e is CompoundFilterEntry => "group" in e)
    .map((e) => e.group.logic);

  const bothCompoundLogicsExist =
    existingCompoundLogics.includes("and") &&
    existingCompoundLogics.includes("or");

  return (
    <div className="flex items-center gap-2 border-t border-divider px-2">
      <div className="flex min-h-target flex-1 items-center gap-3 overflow-x-auto pr-3">
        {activeFilterEntries.map((entry) => {
          if ("group" in entry) {
            return (
              <CompoundFilterChip
                key={entry.group.id}
                group={entry.group}
                filterMenuItemGroups={filterMenuItemGroups}
                onRemoveGroup={onRemoveCompoundGroup}
                onSetCondition={onSetCompoundCondition}
                onAddCondition={onAddConditionToGroup}
                onRemoveCondition={onRemoveCompoundCondition}
                onToggleLogic={onToggleCompoundLogic}
                logicToggleLocked={bothCompoundLogicsExist}
              />
            );
          }
          return (
            <FilterChip
              key={entry.fieldName}
              entry={entry}
              onTextOperatorChange={onTextOperatorChange}
              onComparisonOperatorChange={onComparisonChange}
              onRemove={() => onRemoveFilter(entry.fieldName)}
            />
          );
        })}
        <DropdownMenu.Root open={addFilterOpen} onOpenChange={setAddFilterOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="relative hit-target flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted hover:bg-surface hover:text-muted"
              aria-label={t("filterTable")}
            >
              <PlusRegular />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="min-w-72 rounded-md border border-divider bg-page p-1.5 shadow-lg"
              sideOffset={4}
              align="start"
              side="bottom"
            >
              <FilterMenuContent
                selectedValues={selectedValues}
                comparisonValues={comparisonValues}
                booleanValues={booleanValues}
                onSelectionChange={onSelectionChange}
                onComparisonChange={onComparisonChange}
                onBooleanChange={onBooleanChange}
                filterMenuItemGroups={filterMenuItemGroups}
                onCreateCompoundGroup={onCreateCompoundGroup}
                existingCompoundLogics={existingCompoundLogics}
              />
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      <button
        type="button"
        onClick={onClearAll}
        className="relative hit-target shrink-0 rounded-full border border-default px-3 py-0.5 text-xs text-muted hover:bg-surface"
      >
        {t("filterClear")}
      </button>
    </div>
  );
}
